import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { SidebarMode } from 'src/layouts/SidebarV2/types';

// Mobile = below the DS desktop breakpoint (--breakpoint-desktop: 769px).
const MOBILE_QUERY = '(max-width: 768px)';

const supportsMatchMedia = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

export const useMediaMatch = (query: string): boolean => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!supportsMatchMedia()) {
        return () => undefined;
      }
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query]
  );

  const getSnapshot = useCallback(
    () => (supportsMatchMedia() ? window.matchMedia(query).matches : false),
    [query]
  );

  const getServerSnapshot = () => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

export interface UseSidebarStateOptions {
  initialMode?: SidebarMode;
}

export interface SidebarState {
  isOpen: boolean;
  isMobile: boolean;
  mode: SidebarMode;
  toggle: () => void;
  open: () => void;
  close: () => void;
  setMode: (mode: SidebarMode) => void;
}

export const useSidebarState = ({
  initialMode = SidebarMode.DEFAULT
}: UseSidebarStateOptions = {}): SidebarState => {
  const isMobile = useMediaMatch(MOBILE_QUERY);
  const [isOpen, setIsOpen] = useState<boolean>(!isMobile);
  const [mode, setMode] = useState<SidebarMode>(initialMode);

  // Collapse on mobile, expand on desktop; manual toggles reset on breakpoint cross.
  useEffect(() => {
    setIsOpen(!isMobile);
  }, [isMobile]);

  const toggle = () => setIsOpen((prev) => !prev);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  return { isOpen, isMobile, mode, toggle, open, close, setMode };
};
